import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Requirement.css';
import { toast } from "react-toastify";
import TemplateService from '../utils/templateService'; // Import template service
import {
  MdClose,
  MdCheckCircle,
  MdError,
  MdBusiness,
  MdWork,
  MdPeople,
  MdLocationOn,
  MdTimelapse,
  MdSchool,
  MdAttachMoney,
  MdPerson,
  MdDescription,
  MdCalendarToday,
  MdAdd,
  MdCancel,
  MdAddCircle,
  MdAutoAwesome
} from "react-icons/md";

const Requirement = ({ isOpen, onClose, onRequirementCreated, editRequirement }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    position: '',
    department: '',
    description: '',
    vacancies: '',
    hiringManager: '',
    companyName: '',
    requiredSkills: [],
    location: '',
    experience: '',
    qualification: '',
    salaryRange: '',
    employmentType: 'Full-time',
    workMode: 'On-site',
    deadline: '',
    status: 'Active'
  });
  const [skillsInput, setSkillsInput] = useState('');
  const [extraFields, setExtraFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);

  // Get current user from session storage
  useEffect(() => {
    const getUserFromSession = () => {
      try {
        const userDataString = sessionStorage.getItem('userData');
        
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          console.log('📦 User data from session:', userData);
          
          const userInfo = {
            name: userData.name || userData.userName || 'User',
            company: userData.company || userData.companyName || '',
            email: userData.email
          };
          
          setCurrentUser(userInfo);

          if (!editRequirement) {
            setFormData(prev => ({
              ...prev,
              companyName: userInfo.company || 'DevOpsTrio',
              hiringManager: userInfo.name || 'HR Manager'
            }));
          }
        } else {
          setCurrentUser({
            name: 'Admin',
            company: 'DevOpsTrio',
            email: 'devopstrioadmin@gmail.com'
          });
          if (!editRequirement) {
            setFormData(prev => ({
              ...prev,
              companyName: 'DevOpsTrio',
              hiringManager: 'HR Manager'
            }));
          }
        }
      } catch (error) {
        console.error('❌ Error getting user from session:', error);
      }
    };

    if (isOpen) {
      getUserFromSession();
    }
  }, [isOpen, editRequirement]);

  // Populate form when editing
  useEffect(() => {
    if (editRequirement && isOpen) {
      console.log('📝 Editing requirement:', editRequirement);
      setFormData({
        position: editRequirement.position || '',
        department: editRequirement.department || '',
        description: editRequirement.description || '',
        vacancies: editRequirement.vacancies || '',
        hiringManager: editRequirement.hiringManager || '',
        companyName: editRequirement.companyName || '',
        requiredSkills: editRequirement.requiredSkills || [],
        location: editRequirement.location || '',
        experience: editRequirement.experience || '',
        qualification: editRequirement.qualification || '',
        salaryRange: editRequirement.salaryRange || '',
        employmentType: editRequirement.employmentType || 'Full-time',
        workMode: editRequirement.workMode || 'On-site',
        deadline: editRequirement.deadline ? editRequirement.deadline.split('T')[0] : '',
        status: editRequirement.status || 'Active'
      });
      setExtraFields(editRequirement.fields || []);
    } else if (isOpen && !editRequirement) {
      const userDataString = sessionStorage.getItem('userData');
      let companyName = 'DevOpsTrio';
      let userName = 'HR Manager';

      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          companyName = userData.companyName || userData.company || 'DevOpsTrio';
          userName = userData.name || userData.userName || 'HR Manager';
        } catch (error) {
          console.error('Error parsing userData:', error);
        }
      }

      setFormData({
        position: '',
        department: '',
        description: '',
        vacancies: '',
        hiringManager: userName,
        companyName: companyName,
        requiredSkills: [],
        location: '',
        experience: '',
        qualification: '',
        salaryRange: '',
        employmentType: 'Full-time',
        workMode: 'On-site',
        deadline: '',
        status: 'Active'
      });
      setExtraFields([]);
      setSkillsInput('');
    }
  }, [editRequirement, isOpen]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Template-based auto generation - NO AI NEEDED!
  const handlePositionBlur = () => {
    const positionTitle = formData.position.trim();
    
    if (!positionTitle || positionTitle.length < 3) {
      toast.warning('Please enter at least 3 characters for position title');
      return;
    }
    
    if (isGeneratingTemplate) return;
    
    // Check if we should overwrite existing content
    if (formData.description && formData.description.length > 50 && formData.requiredSkills.length > 0) {
      const confirmRegenerate = window.confirm(
        'Job description and skills already exist. Do you want to auto-generate based on position title?'
      );
      if (!confirmRegenerate) return;
    }
    
    setIsGeneratingTemplate(true);
    
    try {
      console.log('📝 Auto-generating from template for position:', positionTitle);
      
      // Get template from our JSON file
      const template = TemplateService.getTemplate(positionTitle);
      
      if (template) {
        setFormData(prev => ({
          ...prev,
          description: template.description,
          requiredSkills: template.skills
        }));
        
        toast.success(`✅ Auto-generated description and ${template.skills.length} skills for "${positionTitle}"!`, {
          autoClose: 3000
        });
        
        console.log('✅ Template applied:', template);
      } else {
        toast.info('No specific template found. You can manually enter details.', {
          autoClose: 3000
        });
      }
      
    } catch (error) {
      console.error('❌ Template generation error:', error);
      toast.error('Failed to generate from template. Please try again.');
    } finally {
      setIsGeneratingTemplate(false);
    }
  };

  // Manual trigger for template generation (optional button)
  const handleManualGenerate = () => {
    if (!formData.position.trim()) {
      toast.warning('Please enter a position title first');
      return;
    }
    handlePositionBlur();
  };

  // Skills management
  const addSkill = () => {
    const skill = skillsInput.trim();
    if (skill && !formData.requiredSkills.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        requiredSkills: [...prev.requiredSkills, skill]
      }));
      setSkillsInput('');
    }
  };

  const removeSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      requiredSkills: prev.requiredSkills.filter((_, i) => i !== index)
    }));
  };

  const handleSkillKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  // Extra Fields Handlers
  const addExtraField = (type) => {
    const newField = { label: '', type, value: '' };
    if (type === 'dropdown') newField.options = ['Option 1', 'Option 2'];
    setExtraFields([...extraFields, newField]);
  };

  const updateExtraField = (index, key, val) => {
    const updated = [...extraFields];
    updated[index][key] = val;
    setExtraFields(updated);
  };

  const removeExtraField = (index) => {
    setExtraFields(extraFields.filter((_, i) => i !== index));
  };

  // Submit Job Posting
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    if (formData.requiredSkills.length === 0) {
      setErrorMessage('Please add at least one required skill');
      setLoading(false);
      return;
    }

    if (formData.vacancies <= 0) {
      setErrorMessage('Vacancies must be greater than 0');
      setLoading(false);
      return;
    }

    try {
      const token = sessionStorage.getItem('authToken');
      if (!token) {
        setErrorMessage('Please login again');
        setLoading(false);
        return;
      }

      const payload = {
        position: formData.position.trim(),
        department: formData.department.trim(),
        description: formData.description.trim(),
        vacancies: Number(formData.vacancies),
        hiringManager: formData.hiringManager.trim(),
        companyName: formData.companyName.trim(),
        requiredSkills: formData.requiredSkills,
        location: formData.location?.trim() || '',
        experience: formData.experience?.trim() || '',
        qualification: formData.qualification?.trim() || '',
        salaryRange: formData.salaryRange?.trim() || '',
        employmentType: formData.employmentType,
        workMode: formData.workMode,
        deadline: formData.deadline || null,
        fields: extraFields,
        status: 'Active'
      };

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const url = editRequirement 
        ? `${API_BASE_URL}/api/requirements/${editRequirement._id}`
        : `${API_BASE_URL}/api/requirements`;

      const method = editRequirement ? 'put' : 'post';

      await axios[method](url, payload, config);
      
      setSuccessMessage(editRequirement ? 'Job updated successfully!' : 'Job created successfully!');
      toast.success(editRequirement ? 'Job updated!' : 'Job created!');
      
      if (onRequirementCreated) {
        onRequirementCreated();
      }

      setTimeout(() => {
        handleClose();
      }, 1500);

    } catch (err) {
      console.error('❌ Error saving job:', err);
      const errorMsg = err.response?.data?.message || 'Failed to save job';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      position: '',
      department: '',
      description: '',
      vacancies: '',
      hiringManager: currentUser?.name || 'HR Manager',
      companyName: currentUser?.company || 'DevOpsTrio',
      requiredSkills: [],
      location: '',
      experience: '',
      qualification: '',
      salaryRange: '',
      employmentType: 'Full-time',
      workMode: 'On-site',
      deadline: '',
      status: 'Active'
    });
    setSkillsInput('');
    setExtraFields([]);
    setSuccessMessage('');
    setErrorMessage('');
    setIsGeneratingTemplate(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="requirement-popup-overlay">
      <div className="requirement-popup">
        <div className="popup-header">
          <h2>{editRequirement ? 'Edit Job' : 'Create New Job'}</h2>
          <button className="closes-btn" onClick={handleClose}>
            <MdClose size={24} />
          </button>
        </div>

        <div className="popup-content">
          {successMessage && (
            <div className="success-message">
              <MdCheckCircle size={20} className="success-icon" />
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="error-message">
              <MdError size={20} className="error-icon" />
              {errorMessage}
            </div>
          )}

          {currentUser && (
            <div className="current-user-info">
              <div className="user-avatar">
                {currentUser.name.charAt(0).toUpperCase()}
              </div>
              <div className="user-details">
                <strong>{currentUser.name}</strong>
                <span> | {currentUser.company || 'DevOpsTrio'}</span>
              </div>
            </div>
          )}

          <div className="form-container">
            <form className="requirement-form" onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>
                  <MdWork size={20} />
                  Job Details
                </h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <MdBusiness size={16} />
                      Company Name *
                    </label>
                    <input 
                      type="text" 
                      name="companyName" 
                      value={formData.companyName} 
                      onChange={handleChange} 
                      required 
                      placeholder="e.g., DevOpsTrio"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <MdWork size={16} />
                      Position Title *
                    </label>
                    <div className="position-input-wrapper">
                      <input 
                        type="text" 
                        name="position" 
                        value={formData.position} 
                        onChange={handleChange}
                        onBlur={handlePositionBlur}
                        required 
                        placeholder="e.g., Python Developer, Full Stack Developer"
                        disabled={isGeneratingTemplate}
                      />
                      {isGeneratingTemplate && (
                        <div className="template-generating-indicator">
                          <span className="spinner-small"></span>
                          <MdAutoAwesome size={16} />
                          <span>Generating...</span>
                        </div>
                      )}
                    </div>
                    <small className="template-hint">
                      <MdAutoAwesome size={12} /> 
                      Tab out or click outside to auto-generate description & skills from template
                    </small>
                  </div>
                </div>

                {/* Add Manual Generate Button */}
                <div className="form-group">
                  <button 
                    type="button" 
                    className="manual-generate-btn"
                    onClick={handleManualGenerate}
                    disabled={isGeneratingTemplate || !formData.position.trim()}
                  >
                    <MdAutoAwesome size={18} />
                    Generate from Template
                  </button>
                </div>

                {/* Rest of your form fields remain the same */}
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <MdBusiness size={16} />
                      Department *
                    </label>
                    <input 
                      type="text" 
                      name="department" 
                      value={formData.department} 
                      onChange={handleChange} 
                      required 
                      placeholder="e.g., Engineering"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <MdPeople size={16} />
                      Vacancies *
                    </label>
                    <input 
                      type="number" 
                      name="vacancies" 
                      value={formData.vacancies} 
                      onChange={handleChange} 
                      required 
                      min="1" 
                      placeholder="e.g., 3"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <MdLocationOn size={16} />
                      Location
                    </label>
                    <input 
                      type="text" 
                      name="location" 
                      value={formData.location} 
                      onChange={handleChange} 
                      placeholder="e.g., Bangalore, India"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <MdTimelapse size={16} />
                      Experience
                    </label>
                    <input 
                      type="text" 
                      name="experience" 
                      value={formData.experience} 
                      onChange={handleChange} 
                      placeholder="e.g., 3-5 years"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <MdSchool size={16} />
                      Qualification
                    </label>
                    <input 
                      type="text" 
                      name="qualification" 
                      value={formData.qualification} 
                      onChange={handleChange} 
                      placeholder="e.g., B.Tech in CS"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <MdAttachMoney size={16} />
                      Salary Range
                    </label>
                    <input 
                      type="text" 
                      name="salaryRange" 
                      value={formData.salaryRange} 
                      onChange={handleChange} 
                      placeholder="e.g., ₹8L - ₹15L PA"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <MdWork size={16} />
                      Employment Type
                    </label>
                    <select 
                      name="employmentType" 
                      value={formData.employmentType} 
                      onChange={handleChange}
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                      <option value="Remote">Remote</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>
                      <MdWork size={16} />
                      Work Mode
                    </label>
                    <select 
                      name="workMode" 
                      value={formData.workMode} 
                      onChange={handleChange}
                    >
                      <option value="On-site">On-site</option>
                      <option value="Remote">Remote</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <MdPerson size={16} />
                    Hiring Manager *
                  </label>
                  <input 
                    type="text" 
                    name="hiringManager" 
                    value={formData.hiringManager} 
                    onChange={handleChange} 
                    required 
                  />
                </div>

                <div className="form-group">
                  <label>
                    <MdDescription size={16} />
                    Job Description *
                    {isGeneratingTemplate && (
                      <span className="template-badge">Generating...</span>
                    )}
                  </label>
                  <textarea 
                    name="description" 
                    value={formData.description} 
                    onChange={handleChange} 
                    required
                    rows="6"
                    placeholder="Job description will be auto-generated when you enter position title and tab out..."
                    disabled={isGeneratingTemplate}
                  ></textarea>
                </div>

                <div className="form-group">
                  <label>
                    <MdCalendarToday size={16} />
                    Application Deadline
                  </label>
                  <input 
                    type="date" 
                    name="deadline" 
                    value={formData.deadline} 
                    onChange={handleChange} 
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>
                  <MdWork size={20} />
                  Required Skills *
                  {isGeneratingTemplate && (
                    <span className="template-badge">Generating...</span>
                  )}
                </h3>
                
                <div className="form-group">
                  <div className="skills-input-container">
                    <input 
                      type="text" 
                      value={skillsInput}
                      onChange={(e) => setSkillsInput(e.target.value)}
                      onKeyPress={handleSkillKeyPress}
                      placeholder="Enter skills (e.g., JavaScript, React)"
                      className="skills-input"
                      disabled={isGeneratingTemplate}
                    />
                    <button 
                      type="button" 
                      onClick={addSkill}
                      className="add-skill-btn"
                      disabled={isGeneratingTemplate}
                    >
                      <MdAdd size={18} /> Add
                    </button>
                  </div>
                  
                  <div className="skills-tags">
                    {formData.requiredSkills.length > 0 ? (
                      formData.requiredSkills.map((skill, index) => (
                        <span key={index} className="skill-tag">
                          {skill}
                          <button 
                            type="button" 
                            onClick={() => removeSkill(index)}
                            className="remove-skill"
                          >
                            <MdClose size={14} />
                          </button>
                        </span>
                      ))
                    ) : (
                      <div className="empty-skills-message">
                        {isGeneratingTemplate ? 'Generating skills from template...' : 'No skills added yet. Enter position title to auto-generate skills.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleClose} className="cancel-btn">
                  <MdCancel size={18} /> Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading || isGeneratingTemplate}>
                  {loading ? (
                    <>
                      <span className="spinner"></span> Saving...
                    </>
                  ) : editRequirement ? (
                    <>
                      <MdCheckCircle size={18} /> Update Job
                    </>
                  ) : (
                    <>
                      <MdAddCircle size={18} /> Create Job
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Requirement;